import { set } from "date-fns";
import { TotpSecret } from "firebase/auth";
import { useCallback, useState } from "react";
import { useAuth } from "~/login/provider";

export default function useMultifactorAuthSettings() {
  const {
    state,
    reauthenticateForMfA,
    generateMfaSecrets,
    enrollMfa,
    unenrollMfa,
    checkMFAStatus,
    verifyMfaCodeForUnenroll,
    dispatch,
  } = useAuth();
  const { user } = state;
  const signInMethod = user?.providerData[0]?.providerId;
  const enrolledInMFA = useCallback(() => checkMFAStatus(user), [state])();
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [open, setOpen] = useState(false);
  const [mfaData, setMfaData] = useState<{
    qrCodeUrl: string;
    secret: TotpSecret;
  } | null>(null);

  const handleAddTotp = async () => {
    if (!user) {
      throw new Error("No user is currently signed in.");
    }
    setOpen(true);
    dispatch({ type: "clear errors" });
    // Reauthenticate the user before enrolling in TOTP
    await reauthenticateForMfA({ password });
    // After successful reauthentication, proceed to enroll TOTP
    const mfaData = await generateMfaSecrets();
    setMfaData(mfaData);
    // Clear the password field after use
    setPassword("");
  };

  const handleCancelEnrollment = () => {
    setMfaData(null);
    setVerificationCode("");
    setOpen(false);
    dispatch({ type: "clear errors", status: "idle" });
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !mfaData?.secret || !verificationCode) {
      dispatch({
        type: "mfa verification failure",
        error: "No verification code provided.",
        status: "idle",
      });
      throw new Error("No user or verification code for verification");
    }
    try {
      await enrollMfa({
        enrollmentSecret: mfaData.secret,
        verificationCode,
      });
      // Clear the state after successful verification
      setMfaData(null);
      setVerificationCode("");
      // close the accordion
      setOpen(false);
    } catch (error) {
      console.error("Error verifying TOTP:", error);
    }
  };

  const handleRemoveTotp = async () => {
    if (!user) {
      throw new Error("No user is currently signed in.");
    }
    try {
      await unenrollMfa({ password });
      // Clear the state after successful unenrollment
      setMfaData(null);
      setVerificationCode("");
    } catch (error) {}
  };

  const handleVerifyTotpForUnenroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !verificationCode) {
      dispatch({
        type: "mfa verification failure",
        error: "No verification code provided.",
        status: state.status,
      });
      throw new Error("No user or verification code for verification");
    }
    try {
      await verifyMfaCodeForUnenroll({ verificationCode });
      // Clear the state after successful verification
      setMfaData(null);
      setVerificationCode("");
    } catch (error) {
      console.error("Error verifying TOTP for unenroll:", error);
    }
  };

  return {
    accordion: {
      open,
    },
    state,
    user,
    signInMethod,
    enrolledInMFA,
    handleAddTotp,
    handleVerifyTotp,
    handleCancelEnrollment,
    handleRemoveTotp,
    handleVerifyTotpForUnenroll,
    password,
    setPassword,
    mfaData,
    verificationCode,
    setVerificationCode,
  };
}
